import { signalStore, withMethods, withState } from '@ngrx/signals';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { Conversation, ConversationIncludeOption, ConversationPostRequest } from '@clientwire/api';
import { inject } from '@angular/core';
import {
  removeAllEntities,
  removeEntity,
  setAllEntities,
  setEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { ConversationFilters } from '../interfaces';
import { WireApiService } from '../services';
import { logger } from '../utils';
import { ErrorStore, ResponseError } from './error.store';

export const ConversationStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withDevtools('conversation-store'),
  withState({
    filterString: '',
    showArchiveOnly: false,
    error: null,
    loadingConversations: false,
    totalElements: 0,
  }),
  withEntities<Conversation>(),
  withMethods((store) => {
    const wireApiService = inject(WireApiService);
    const errorStore = inject(ErrorStore);

    return {
      async getConversations(limit = 10, offset = 0) {
        updateState(store, 'Set loadingConversation to true', { loadingConversations: true });

        errorStore.clearErrorsByLocations(['getConversations']);

        const response = await wireApiService.conversationsApi
          .getConversations({
            limit,
            offset,
            archived: store.showArchiveOnly(),
            search: store.filterString(),
            includes: [
              ConversationIncludeOption.Participants,
              ConversationIncludeOption.ConversationType,
              ConversationIncludeOption.ListItemTemplateData,
              ConversationIncludeOption.ClientParticipantFrontendUrl,
            ],
          })
          .catch((err) => {
            console.error('BEFORE LOGIN ERROR:', err);
            errorStore.addErrorResponse(err, 'inline', false, 'getConversations');
            updateState(store, 'Set loadingConversation to false', { loadingConversations: false });
            return err;
          });

        if (response.conversations?.length > 0) {
          if (offset === 0) {
            updateState(
              store,
              'Set all conversations in ConversationStore',
              setAllEntities(response.conversations)
            );
          } else {
            updateState(
              store,
              'Set conversations in ConversationStore',
              setEntities(response.conversations)
            );
          }
        } else {
          if (response.totalElements === 0) {
            updateState(
              store,
              'Remove all conversation from ConversationStore',
              removeAllEntities()
            );
          }
        }
        updateState(store, 'Set totalElements of ConversationStore', {
          totalElements: response.totalElements ?? 0,
        });
        updateState(store, 'Set loadingConversation to false', { loadingConversations: false });
      },

      async getConversation(conversationId: string, includes?: ConversationIncludeOption[]) {
        errorStore.clearErrorsByLocations([`getConversation:${conversationId}`]);
        try {
          const response = await wireApiService.conversationsApi.getConversation({
            conversationId,
            includes,
          });
          updateState(
            store,
            'Add conversation to ConversationStore',
            setAllEntities([response, ...store.entities()])
          );
        } catch (error) {
          try {
            await errorStore.addErrorResponse(
              error,
              'global',
              true,
              `getConversation:${conversationId}`,
              'error.global.title',
              'error.conversation.get',
              'error.conversation.get'
            );
          } catch {
            await errorStore.addErrorResponse(
              { code: `error.conversation.get` },
              'global',
              false,
              `getConversation:${conversationId}`
            );
          }
        }
      },

      clearError() {
        updateState(store, 'Clear errors in ConversationStore', { error: null });
      },

      async createNewConversation(
        newConversation: ConversationPostRequest,
        includes?: ConversationIncludeOption[]
      ): Promise<Conversation | null> {
        try {
          errorStore.clearErrorsByLocations(['createConversation']);
          const response = await wireApiService.conversationsApi.createConversation({
            conversationPostRequest: newConversation,
            includes,
          });

          updateState(
            store,
            'Add conversation to ConversationStore',
            setAllEntities([response, ...store.entities()])
          );

          return response;
        } catch (err) {
          const jsonResponse = await (err as ResponseError).response?.json();
          const errorMessage = jsonResponse?.message;
          if (errorMessage) {
            errorStore.addErrorResponse(jsonResponse, 'inline', false, 'createConversation');
          } else {
            logger.error(err);
          }
          return null;
        }
      },

      addConversation(conversation: Conversation, sort: 'asc' | 'desc' = 'desc') {
        const conversations = store.entities();
        if (conversations.find((c) => c.id === conversation.id)) {
          return;
        }
        const index = conversations.findIndex((c) => {
          if (!c.createdAt || !conversation.createdAt) {
            return false;
          }
          if (sort === 'asc') {
            return c.createdAt > conversation.createdAt;
          } else {
            return c.createdAt < conversation.createdAt;
          }
        });

        if (index === -1) {
          conversations.push(conversation);
        } else {
          conversations.splice(index, 0, conversation);
        }

        updateState(store, 'Add conversation to ConversationStore', setAllEntities(conversations));
      },

      changeFilters(filters: ConversationFilters) {
        updateState(store, 'Change filters in ConversationStore', {
          showArchiveOnly: filters.showArchived,
          filterString: filters.search,
        });
      },

      async archiveConversation(id: string) {
        await wireApiService.conversationsApi.updateConversation({
          conversationId: id,
          conversationPatchRequest: { archived: true },
        });

        updateState(
          store,
          'Update conversation in ConversationStore',
          updateEntity({ id, changes: { archived: true } })
        );
      },

      async unarchiveConversation(id: string) {
        await wireApiService.conversationsApi.updateConversation({
          conversationId: id,
          conversationPatchRequest: { archived: false },
        });

        updateState(
          store,
          'Update conversation in ConversationStore',
          updateEntity({ id, changes: { archived: false } })
        );
      },

      updateConversation(conversationId: string, changes: Partial<Conversation>) {
        updateState(
          store,
          'Update conversation in ConversationStore',
          updateEntity({ id: conversationId, changes: { ...changes } })
        );
      },

      removeConversation(conversationId: string) {
        updateState(
          store,
          'Remove conversation from ConversationStore',
          removeEntity(conversationId)
        );
      },

      clearConversations() {
        updateState(store, 'Clear ConversationStore', {
          filterString: '',
          showArchiveOnly: false,
          error: null,
          loadingConversations: false,
          totalElements: 0,
        });
        updateState(store, 'Clear entities', removeAllEntities());
      },
    };
  })
);
